import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  getDefaultRedirectPath,
  ROLE_PERMISSIONS,
  ROUTE_PERMISSIONS,
  type RoleId,
} from "../../types/permissions";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Pre-fill with test tenant ID by default
  const [tenantId, setTenantId] = useState(
    "c42b71e6-fe42-428f-bb86-b0e39676a0cb",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Validate required fields
    if (!email || !password) {
      setError("Email y contraseña son requeridos");
      setIsLoading(false);
      return;
    }

    if (!tenantId) {
      setError("Tenant ID es requerido");
      setIsLoading(false);
      return;
    }

    try {
      await login({
        email,
        password,
        tenantId,
      });

      const fromPath = (
        location.state as { from?: { pathname?: string } } | null
      )?.from?.pathname;

      const roleId = Number(
        JSON.parse(localStorage.getItem("poolchem_user") || "{}").roleId,
      );
      const normalizedRoleId = Number.isFinite(roleId)
        ? (roleId as RoleId)
        : undefined;
      const fallbackPath = normalizedRoleId
        ? getDefaultRedirectPath(normalizedRoleId)
        : "/";

      const fromPermission = fromPath ? ROUTE_PERMISSIONS[fromPath] : undefined;
      const rolePermissions = normalizedRoleId
        ? ROLE_PERMISSIONS[normalizedRoleId] || []
        : [];
      const canAccessFromPath = fromPath
        ? !fromPermission || rolePermissions.includes(fromPermission)
        : false;

      navigate(canAccessFromPath ? fromPath : fallbackPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar sesión");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img
              src="https://public.readdy.ai/ai/img_res/a767d65d-0e19-49c9-9380-a6816d196f72.png"
              alt="PoolChem Logo"
              className="w-16 h-16 object-contain"
            />
            <div className="text-left">
              <h1 className="text-3xl font-bold text-teal-600">PoolChem</h1>
              <p className="text-lg text-teal-500 font-medium">Monitor</p>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Iniciar Sesión
          </h2>
          <p className="text-gray-600">
            Accede a tu sistema de monitoreo de piscinas
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                <i className="ri-error-warning-line text-lg"></i>
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400"></i>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-gray-900 placeholder-gray-400"
                  placeholder="usuario@ejemplo.com"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-gray-900 placeholder-gray-400"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i
                    className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-gray-400 hover:text-gray-600 transition-colors`}
                  ></i>
                </button>
              </div>
            </div>

            {/* Tenant ID Field (Optional) */}
            <div>
              <label
                htmlFor="tenantId"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                ID del Inquilino{" "}
                <span className="text-gray-400 text-xs">(opcional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-building-line text-gray-400"></i>
                </div>
                <input
                  id="tenantId"
                  name="tenantId"
                  type="text"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 text-gray-900 placeholder-gray-400"
                  placeholder="c42b71e6-fe42-428f-bb86-b0e39676a0cb"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Iniciando sesión...
                  </>
                ) : (
                  <>
                    <i className="ri-login-circle-line mr-2"></i>
                    Iniciar Sesión
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <div className="text-sm text-gray-600">
              ¿Problemas para acceder?{" "}
              <a
                href="#"
                className="font-medium text-teal-600 hover:text-teal-500 transition-colors"
              >
                Contactar soporte
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2025 PoolChem Monitor. Sistema de monitoreo profesional de
            piscinas.
          </p>
        </div>
      </div>
    </div>
  );
}
